import React, { useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Calendar as CalendarIcon, Clock, User, Package, MapPin } from 'lucide-react';
import { Booking } from '../../types';
import { supabase } from '../../utils/supabase';

export function Calendar() {
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = React.useState<Booking | null>(null);
  const [orders, setOrders] = React.useState<Booking[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  // Fetch orders and set up real-time subscription
  React.useEffect(() => {
    fetchOrders();

    const subscription = supabase
      .channel('orders_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        () => {
          fetchOrders();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('status', 'Confirmed Install Date')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format orders for calendar events
  const calendarEvents = useMemo(() => {
    return orders
      .filter((order) => 
        order.status === 'Confirmed Install Date' && 
        order.delivery_schedule?.date &&
        order.delivery_schedule?.time
      )
      .map((order) => {
        const [startTime] = order.delivery_schedule!.time.split(' - ');
        const dateTime = `${order.delivery_schedule!.date}T${format(new Date(`2000/01/01 ${startTime}`), 'HH:mm:ss')}`;
        
        return {
          id: order.id,
          title: `${order.customer_name} - ${order.product.name}`,
          start: dateTime,
          end: new Date(new Date(dateTime).getTime() + 2 * 60 * 60 * 1000).toISOString(), // 2 hour duration
          extendedProps: order
        };
      });
  }, [orders]);

  const handleEventClick = (info: any) => {
    const booking = info.event.extendedProps;
    setSelectedEvent(booking);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Installation Calendar</h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={calendarEvents}
          eventClick={handleEventClick}
          slotMinTime="09:00:00"
          slotMaxTime="17:00:00"
          allDaySlot={false}
          height="auto"
          expandRows={true}
          stickyHeaderDates={true}
          dayMaxEvents={true}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
        />
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <Dialog.Root open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
            <Dialog.Content className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-full max-w-lg bg-white rounded-lg shadow-lg">
              {selectedEvent && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                      Installation Details
                    </Dialog.Title>
                    <Dialog.Close className="text-gray-400 hover:text-gray-500">
                      <X className="w-5 h-5" />
                    </Dialog.Close>
                  </div>

                  <div className="space-y-6">
                    {/* Order Information */}
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">Order #{selectedEvent.order_number}</div>
                      <button
                        onClick={() => {
                          setSelectedEvent(null);
                          navigate('/admin/orders');
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        View Full Details
                      </button>
                    </div>

                    {/* Installation Time */}
                    <div className="flex items-center space-x-3 text-gray-700">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="font-medium">
                          {format(new Date(selectedEvent.delivery_schedule!.date), 'MMMM d, yyyy')}
                        </div>
                        <div className="text-sm text-gray-500">
                          {selectedEvent.delivery_schedule!.time}
                        </div>
                      </div>
                    </div>

                    {/* Customer Information */}
                    <div className="flex items-start space-x-3 text-gray-700">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="font-medium">{selectedEvent.customer_name}</div>
                        <div className="text-sm text-gray-500">{selectedEvent.customer_phone}</div>
                        {selectedEvent.customer_email && (
                          <div className="text-sm text-gray-500">{selectedEvent.customer_email}</div>
                        )}
                      </div>
                    </div>

                    {/* Product Information */}
                    <div className="flex items-start space-x-3 text-gray-700">
                      <Package className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="font-medium">{selectedEvent.product.name}</div>
                        <div className="text-sm text-gray-500">
                          {selectedEvent.product.brand} - ${selectedEvent.product.price}
                        </div>
                      </div>
                    </div>

                    {/* Installation Location */}
                    <div className="flex items-start space-x-3 text-gray-700">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="font-medium">Installation Location</div>
                        <div className="text-sm text-gray-500">
                          {selectedEvent.installation_location}
                        </div>
                        <div className="text-sm text-gray-500">
                          {selectedEvent.delivery_address.street}<br />
                          {selectedEvent.delivery_address.city}, {selectedEvent.delivery_address.state} {selectedEvent.delivery_address.zipCode}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}